import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchBlogs } from "../utils/blogsApi";
import Header from "../components/header/Header";
import Footer from "../components/footer/Footer";

const AuthorAvatar = ({ name, className }) => (
  <span
    className={`flex items-center justify-center rounded-full bg-[#EFF4FF] font-bold uppercase text-[#5072F1] ${className}`}
  >
    {(name || "G").charAt(0)}
  </span>
);

export default function BlogsPage() {
  const navigate = useNavigate();
  const [blogsData, setBlogsData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("loading");
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();

    setStatus("loading");
    fetchBlogs({ page, signal: controller.signal })
      .then(({ blogs, pagination: meta }) => {
        setBlogsData(blogs);
        setPagination(meta);
        setStatus("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        console.error("Failed to load blogs", error);
        setStatus("error");
      });

    return () => controller.abort();
  }, [page, reloadToken]);

  // Only promote a hero when there are other posts left to fill the grid below it,
  // otherwise a single post would render as a hero above an empty grid.
  const heroArticle = page === 1 && blogsData.length > 1 ? blogsData[0] : null;
  const gridArticles = heroArticle
    ? blogsData.filter((b) => b.id !== heroArticle.id)
    : blogsData;

  const totalPages = pagination?.total_pages || 1;

  return (
    <div className="min-h-screen flex flex-col justify-between bg-white text-[#242424] font-sans antialiased">
      <div className="flex-1 flex flex-col">
        {/* Main Navbar */}
        <Header />

        {/* Main Content Area */}
        <main className="container mx-auto px-4 md:px-8 py-10 max-w-6xl flex-1 flex flex-col justify-center">
          {status === "loading" && (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <span className="w-8 h-8 rounded-full border-2 border-gray-200 border-t-[#5072F1] animate-spin" />
              <p className="text-xs text-gray-500">Loading stories…</p>
            </div>
          )}

          {status === "error" && (
            <div className="text-center py-20 px-8 bg-[#FFF7F7] rounded-3xl border border-red-100 my-10 max-w-2xl mx-auto">
              <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
                Couldn't Load Blog Posts
              </h3>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-md mx-auto mb-5">
                Something went wrong while fetching the latest articles. Please
                try again.
              </p>
              <button
                onClick={() => setReloadToken((token) => token + 1)}
                className="px-4 py-2 bg-[#5072F1] text-white text-xs font-semibold rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}

          {status === "ready" && (
            <>
              {/* Hero Top Featured Section */}
              {heroArticle && (
                <section className="mb-10 border-b border-[#F2F2F2] pb-8">
                  <div
                    onClick={() => navigate(`/blogs/${heroArticle.slug}`)}
                    className="group cursor-pointer grid grid-cols-1 lg:grid-cols-12 gap-6 items-center"
                  >
                    {/* Left Featured Image */}
                    <div className="lg:col-span-5 overflow-hidden rounded-lg bg-gray-100 max-h-[220px] md:max-h-[250px] w-full shadow-sm">
                      <img
                        src={heroArticle.image}
                        alt={heroArticle.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>

                    {/* Right Content */}
                    <div className="lg:col-span-7 flex flex-col justify-center gap-2.5">
                      <h1 className="font-serif text-lg md:text-xl lg:text-[24px] font-bold leading-snug text-[#1A1A1A] group-hover:text-[#5072F1] transition-colors">
                        {heroArticle.title}
                      </h1>

                      <p className="text-gray-600 text-xs md:text-sm leading-relaxed font-sans line-clamp-3">
                        {heroArticle.subtitle}
                      </p>

                      {/* Author Info */}
                      <div className="flex items-center gap-2.5 pt-1">
                        <AuthorAvatar
                          name={heroArticle.author.name}
                          className="w-6 h-6 text-[10px]"
                        />
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span className="font-semibold text-gray-800">
                            {heroArticle.author.name}
                          </span>
                          <span>·</span>
                          <span>{heroArticle.date}</span>
                          <span>·</span>
                          <span>{heroArticle.readTime}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Secondary 3-Column Grid Section */}
              <div className="w-full">
                {blogsData.length > 0 && (
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-base md:text-lg font-bold text-[#1A1A1A]">
                      Latest Stories
                    </h2>
                    <span className="text-xs text-gray-400 font-normal">
                      Showing {blogsData.length} of{" "}
                      {pagination?.count ?? blogsData.length} posts
                    </span>
                  </div>
                )}

                {blogsData.length === 0 ? (
                  <div className="text-center py-20 px-8 bg-[#FAFAFC] rounded-3xl border border-gray-100/80 my-10 max-w-2xl mx-auto shadow-xs flex flex-col items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-blue-50/80 border border-blue-100 flex items-center justify-center text-[#5072F1] mb-5 shadow-inner">
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.6"
                          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-extrabold text-gray-900 mb-2 tracking-tight">
                      No Blog Posts Published Yet
                    </h3>
                    <p className="text-xs md:text-sm text-gray-500 leading-relaxed max-w-md">
                      Check back soon for upcoming VPN security guides, privacy
                      articles, and product announcements.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {gridArticles.map((article) => (
                      <article
                        key={article.id}
                        onClick={() => navigate(`/blogs/${article.slug}`)}
                        className="group cursor-pointer flex flex-col justify-between rounded-lg bg-white border border-[#EFEFEF] hover:border-gray-300 hover:shadow-sm transition-all duration-300 overflow-hidden"
                      >
                        <div>
                          {/* Cover Thumbnail */}
                          <div className="h-[140px] md:h-[150px] overflow-hidden bg-gray-100 w-full relative">
                            <img
                              src={article.image}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>

                          {/* Body Info */}
                          <div className="p-4 flex flex-col gap-2">
                            <h3 className="font-serif text-sm md:text-base font-bold leading-snug text-[#1A1A1A] group-hover:text-[#5072F1] transition-colors line-clamp-2">
                              {article.title}
                            </h3>

                            <p className="text-xs text-gray-500 leading-relaxed font-sans line-clamp-2">
                              {article.subtitle}
                            </p>
                          </div>
                        </div>

                        {/* Footer Author Row */}
                        <div className="px-4 pb-4 pt-2 border-t border-[#F5F5F5] flex items-center gap-2 text-[11px] text-gray-400">
                          <AuthorAvatar
                            name={article.author.name}
                            className="w-5 h-5 text-[9px] shrink-0"
                          />
                          <div className="flex items-center gap-1 truncate">
                            <span className="font-semibold text-gray-700 truncate">
                              {article.author.name}
                            </span>
                            <span>·</span>
                            <span className="whitespace-nowrap">
                              {article.date}
                            </span>
                            <span>·</span>
                            <span className="whitespace-nowrap">
                              {article.readTime}
                            </span>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-10">
                    <button
                      disabled={page <= 1}
                      onClick={() => {
                        setPage((current) => Math.max(1, current - 1));
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      ← Previous
                    </button>
                    <span className="text-xs text-gray-500">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      disabled={page >= totalPages}
                      onClick={() => {
                        setPage((current) => current + 1);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Main Footer */}
      <Footer />
    </div>
  );
}
